import { useDraggable, useDroppable } from "@dnd-kit/core";
import { GridRow, type GridRowProps } from "@mui/x-data-grid/components";
import clsx from "clsx";
import {
  createContext,
  forwardRef,
  useContext,
  type MouseEvent,
  type SyntheticEvent,
} from "react";

import type { DocumentsTableRow } from "../DocumentsTable.types";
import {
  getDocumentsTableItemId,
  getDocumentsTableRowDropTargetId,
  type DocumentsTableItemId,
} from "./DocumentsTable.dnd";
import { isDocumentsTableInteractiveEventTarget } from "./useDocumentsTableSelection";

export type DocumentsTableDnDRowContextValue = {
  activeDragItemId: DocumentsTableItemId | null;
  disabled: boolean;
  isDropTargetEnabled: (itemId: DocumentsTableItemId) => boolean;
  onRowContextMenu: (
    row: DocumentsTableRow,
    event: MouseEvent<HTMLDivElement>
  ) => void;
  selectedItemIds: Set<DocumentsTableItemId>;
};

export const DocumentsTableDnDRowContext =
  createContext<DocumentsTableDnDRowContextValue | null>(null);

const useDocumentsTableDnDRowContext = () => {
  const context = useContext(DocumentsTableDnDRowContext);

  if (!context) {
    throw new Error("DocumentsTableDnDRow context is missing");
  }

  return context;
};

export const DocumentsTableDnDRow = forwardRef<HTMLDivElement, GridRowProps>(
  (props, ref) => {
    const row = props.row as DocumentsTableRow;
    const itemId = getDocumentsTableItemId(row.item);
    const dropTargetId = getDocumentsTableRowDropTargetId(itemId);
    const {
      activeDragItemId,
      disabled,
      isDropTargetEnabled,
      onRowContextMenu,
      selectedItemIds,
    } = useDocumentsTableDnDRowContext();
    const {
      attributes,
      isDragging,
      listeners,
      setNodeRef: setDraggableNodeRef,
    } = useDraggable({
      id: itemId,
      disabled,
    });
    const { isOver, setNodeRef: setDroppableNodeRef } = useDroppable({
      id: dropTargetId,
      disabled,
    });
    const isSelected = selectedItemIds.has(itemId);
    const canDrop = isDropTargetEnabled(itemId);
    const isActiveDropTarget = isOver && canDrop;
    const {
      role: _dragRole,
      tabIndex: _dragTabIndex,
      ...dragAttributes
    } = attributes;
    const guardedListeners =
      listeners &&
      Object.fromEntries(
        Object.entries(listeners).map(([eventName, listener]) => [
          eventName,
          (event: SyntheticEvent) => {
            if (
              eventName === "onPointerDown" &&
              isDocumentsTableInteractiveEventTarget(event.target)
            ) {
              return;
            }

            return listener(event);
          },
        ])
      );

    const setCombinedNodeRef = (node: HTMLDivElement | null) => {
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
      setDraggableNodeRef(node);
      setDroppableNodeRef(node);
    };
    const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
      props.onContextMenu?.(event);

      if (event.defaultPrevented) {
        return;
      }

      onRowContextMenu(row, event);
    };

    return (
      <GridRow
        {...props}
        {...dragAttributes}
        {...guardedListeners}
        ref={setCombinedNodeRef}
        aria-selected={isSelected ? "true" : props["aria-selected"]}
        className={clsx(props.className, {
          "documents-table-row--drop-target": isActiveDropTarget,
          "documents-table-row--dragging": isDragging,
          "documents-table-row--selected": isSelected,
        })}
        data-documents-table-item-id={itemId}
        data-documents-table-selected={isSelected ? "true" : undefined}
        data-testid="documents-table-row"
        onContextMenu={handleContextMenu}
        style={{
          ...props.style,
          opacity: isDragging || activeDragItemId === itemId ? 0.35 : undefined,
        }}
      />
    );
  }
);

DocumentsTableDnDRow.displayName = "DocumentsTableDnDRow";
